import { transition, trigger, useAnimation } from '@angular/animations';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Params, Router } from '@angular/router';
import { IRootState } from '@mnz-core/modules/store/models/root-state';
import { createEmailForm } from '@mnz-modules/login/login.constants';
import * as accountActions from '@mnz-modules/login/store/actions/account.actions';
import * as accountSelectors from '@mnz-modules/login/store/selectors/account.selectors';
import { formUtils } from '@mnz-shared/shared.constants';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { fadeIn } from 'ng-animate';
import { Observable, of } from 'rxjs';
import * as routerSelectors from '@mnz-core/modules/store/selectors/router.selectors';
import { map } from 'rxjs/operators';
import { AccountType } from '@mnz-core/models/_enums';

@Component({
  selector: 'mnz-activate-account',
  templateUrl: 'activate-account.page.html',
  animations: [trigger('fadeIn', [transition('* => *', useAnimation(fadeIn, { params: { timing: 0.5 } }))])],
})
export class ActivateAccountPage {
  @ViewChild('activateAccountInput') activateAccountInput: ElementRef;

  emailErrorMessage: Observable<string>;

  readonly accountType$ = this._store$.pipe(
    select(routerSelectors.getParams),
    map<Params, AccountType>(({ account }) => account)
  );

  constructor(
    private readonly _router: Router,
    private readonly _store$: Store<IRootState>,
    private readonly _translate: TranslateService,
    private readonly _formBuilder: FormBuilder
  ) {}

  readonly emailForm$ = createEmailForm(this._formBuilder);
  readonly loading$ = this._store$.pipe(select(accountSelectors.getLoading));

  public onKeyDown(form, event) {
    if (event.detail.event.key === 'Enter') {
      this.activateAccount(form);
    }
  }

  backToOverview(): void {
    this._router.navigate(['/']);
  }

  activateAccount(form): void {
    if (form.status === 'INVALID') {
      if (form.controls.email.hasError('required')) {
        this.checkEmail(form);
        this.activateAccountInput.nativeElement.focus();
      }
      return formUtils.touchControls({ controls: form.controls.email });
    }

    this._store$.dispatch(
      accountActions.activateAccount({
        email: String(form.controls.email.value).toLowerCase(),
      })
    );
  }

  public checkEmail(form) {
    const email = form.controls.email;
    const isTouchedDirtyAndInvalid = !email.valid && email.touched && email.dirty;

    if (isTouchedDirtyAndInvalid) {
      if (email.hasError('email') && !email.hasError('required')) {
        this.emailErrorMessage = this._translate.get(`LOGIN.INPUT_ERROR.INVALID_EMAIL`);
      } else if (email.hasError('required')) {
        this.emailErrorMessage = this._translate.get(`LOGIN.INPUT_ERROR.REQUIRED_EMAIL`);
      } else if (email.hasError('maxlength') && !email.hasError('pattern')) {
        this.emailErrorMessage = this._translate.get(`LOGIN.INPUT_ERROR.LENGTH_EMAIL`);
      }
    } else {
      this.emailErrorMessage = of('');
    }
  }
}
